import { useEffect, useState } from "react";
import {
  useGetHomeDataStudentLazyQuery,
  useGetResourcesLazyQuery,
} from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { REQUIREMENT } from "helpers/constants";
import { filterFeedbackStats, getFeedbackForChart } from "utils/feedback";
import { Resource } from "types/resource";
import { SPECIAL_TAGS } from "types/tag";
import { getMetricsSinceDate, DEFAULT_METRICS_DATE_RANGE } from "pages/Metrics/metricsDateRange";
import { excludeTestOrganizationVariables } from "utils/testOrganization";

/**
 * Fetches and transforms home data for a specific learner (e.g. when a coach views a learner).
 * Returns data in the same shape as useHome for a student, for use with EPACompetencyRequirements,
 * FeedbackOverview, and RecentFeedbackActivity.
 */
export const useLearnerHomeData = (learnerUserId: string | undefined) => {
  const organization = useAppSelector(organizationSelectedSelector);
  const [allResources, setAllResources] = useState<Resource[]>([]);
  const [getHomeDataStudent, { data: dataStudent, loading: loadingStudent }] =
    useGetHomeDataStudentLazyQuery();
  const [getAllResources, { data: dataResources }] = useGetResourcesLazyQuery();

  const [learnerData, setLearnerData] = useState<any>(null);
  const [learnerStatsData, setLearnerStatsData] = useState<any>(null);

  useEffect(() => {
    setAllResources((dataResources?.resource as Resource[]) || []);
  }, [dataResources]);

  useEffect(() => {
    if (learnerUserId) {
      getHomeDataStudent({
        variables: {
          userId: learnerUserId,
          sinceDate: getMetricsSinceDate(DEFAULT_METRICS_DATE_RANGE),
          ...excludeTestOrganizationVariables,
        },
        fetchPolicy: "cache-and-network",
      });
      getAllResources();
    } else {
      setLearnerData(null);
      setLearnerStatsData(null);
    }
  }, [learnerUserId, getHomeDataStudent, getAllResources]);

  useEffect(() => {
    if (!dataStudent || !learnerUserId) return;

    const reqs =
      dataStudent?.graduation_class?.[0]?.current_academic_year?.requirements?.map(
        (r: any) => r.requirement,
      ) || [];
    const epas = reqs.filter(
      (req: any) => req?.type === REQUIREMENT.REQUIREMENT_EPA,
    );
    const competencies = reqs.filter(
      (req: any) => req?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
    );
    const iters = reqs.filter(
      (req: any) => req?.type === REQUIREMENT.REQUIREMENT_ITER,
    );
    const requirementsResources = reqs
      .map((r: any) => r.resources?.map((c: any) => c.resource) || [])
      .flat();
    const filteredResources = allResources.filter((resource: Resource) =>
      [organization?.name, SPECIAL_TAGS.QUICK_ACCESS].every((tagName) =>
        resource.tags?.some(
          (tagContainer: any) => tagContainer.tag.name === tagName,
        ),
      ),
    );
    const resources = [...requirementsResources, ...filteredResources].filter(
      (resource: any, index: number, self: any[]) =>
        index ===
        self.findIndex((r: any) => r?.resource_id === resource?.resource_id),
    );

    const feedbacksApproved: Record<string, number> = {};
    dataStudent?.feedbacksApproved?.forEach((f: any) => {
      if (!feedbacksApproved[f.feedback_skills?.[0]?.requirement_id]) {
        feedbacksApproved[f.feedback_skills?.[0]?.requirement_id] =
          f.feedback_skills?.[0]?.coach_rating ?? -1;
      }
    });

    const feedbacksByRequirementId: Record<string, any[]> = {};
    dataStudent?.feedbackStatsData?.forEach((feedback: any) => {
      const rid = feedback.feedback_skills?.[0]?.requirement_id;
      if (!rid) return;
      if (!feedbacksByRequirementId[rid]) feedbacksByRequirementId[rid] = [];
      feedbacksByRequirementId[rid].push(feedback);
    });

    const feedbackStatsByRequirementId: Record<string, any> = {};
    Object.keys(feedbacksByRequirementId).forEach((key) => {
      const filteredFeedbackStats = filterFeedbackStats(
        feedbacksByRequirementId[key],
        true,
      );
      feedbackStatsByRequirementId[key] = {
        totalFeedbacks: filteredFeedbackStats.length,
        currentRate: Math.max(
          ...filteredFeedbackStats.map((stat: any) => stat.value),
          0,
        ),
        data: getFeedbackForChart(filteredFeedbackStats),
      };
    });
    // Aggregate score progress per ITER (feedbacks whose skills are all under this ITER's competencies)
    iters.forEach((iter: any) => {
      const competencyIds =
        iter.competencies
          ?.map((c: any) => c.competency?.requirement_id)
          .filter(Boolean) || [];
      if (competencyIds.length === 0) return;
      const feedbacksForIter = (dataStudent?.feedbackStatsData ?? []).filter(
        (f: any) =>
          f.feedback_skills?.length > 0 &&
          f.feedback_skills.every((s: any) =>
            competencyIds.includes(s.requirement_id),
          ),
      );
      if (feedbacksForIter.length === 0) return;
      const filteredFeedbackStats = filterFeedbackStats(
        feedbacksForIter as any,
        true,
      );
      const rates = filteredFeedbackStats.map((s: any) => s.value);
      feedbackStatsByRequirementId[iter.requirement_id] = {
        totalFeedbacks: filteredFeedbackStats.length,
        currentRate: rates.length > 0 ? Math.max(...rates) : 0,
        data: getFeedbackForChart(filteredFeedbackStats),
      };
      feedbacksByRequirementId[iter.requirement_id] = feedbacksForIter;
    });

    const totalEPAAttemptsRequired = epas.reduce(
      (sum: number, epa: any) => sum + (epa.min_performance || 1),
      0,
    );
    const totalCompetencyAttemptsRequired = competencies.reduce(
      (sum: number, comp: any) => sum + (comp.min_performance || 1),
      0,
    );
    const totalAttemptsRequired =
      totalEPAAttemptsRequired + totalCompetencyAttemptsRequired;

    // Completed = sum per requirement of min(accepted feedback count for that requirement, requirement.min_performance)
    const requirementsWithMin = [
      ...epas.map((epa: any) => ({
        requirement_id: epa.requirement_id,
        min: epa.min_performance || 1,
      })),
      ...competencies.map((comp: any) => ({
        requirement_id: comp.requirement_id,
        min: comp.min_performance || 1,
      })),
    ];
    const acceptedCountByRequirementId: Record<string, number> = {};
    dataStudent?.feedbackStatsData?.forEach((feedback: any) => {
      if (feedback.status !== "DONE") return;
      const rid = feedback.feedback_skills?.[0]?.requirement_id;
      if (!rid) return;
      acceptedCountByRequirementId[rid] =
        (acceptedCountByRequirementId[rid] ?? 0) + 1;
    });
    const attemptsCompleted = requirementsWithMin.reduce(
      (
        sum: number,
        { requirement_id, min }: { requirement_id: string; min: number },
      ) => {
        const accepted = acceptedCountByRequirementId[requirement_id] ?? 0;
        return sum + Math.min(accepted, min);
      },
      0,
    );

    const totalForDisplay = Math.max(totalAttemptsRequired, 1);
    const completedForDisplay = Math.min(attemptsCompleted, totalForDisplay);

    const epaCompletionRate =
      totalForDisplay > 0
        ? {
            completed: completedForDisplay,
            total: totalForDisplay,
            displayText: `${completedForDisplay}/${totalForDisplay}`,
          }
        : undefined;

    setLearnerData({
      feedbacks: dataStudent?.feedbacks,
      pendingFeedbacks: dataStudent?.pendingFeedbacks,
      competencyRequirements: competencies,
      epaRequirements: epas,
      iterRequirements: iters,
      allRequirements: reqs,
      resources,
      feedbacksApproved,
      feedbacksByRequirementId,
      feedbackStatsByRequirementId,
      epaCompletionRate,
    });
    setLearnerStatsData(dataStudent?.feedbackStatsData ?? null);
  }, [dataStudent, allResources, organization?.name]);

  return {
    data: learnerData,
    statsData: learnerStatsData,
    loading: loadingStudent,
  };
};
